package pages.outbound

import api.ApiService
import api.dataclasses.outbound.OutboundOrderDetailed
import components.routing.ProtectedRoutingProps
import components.tableCard.TableCard
import components.verticalDetailsCard.VerticalDetailCard
import ics.materialUI.components.StyledBackDrop
import ics.materialUI.components.autoTable.AutoTable
import ics.materialUI.extensions.xs
import ics.utils.promise.launch
import mui.material.Alert
import mui.material.AlertColor
import mui.material.Grid
import mui.system.responsive
import pages.outbound.OutboundDetailPageFieldMappings
import react.FC
import react.router.useParams
import react.useEffect
import react.useState
import kotlin.js.Date

val OutboundOrderDetailedPage = FC<ProtectedRoutingProps>("OutboundOrderDetailedPage") { props ->
    // state hooks
    val (errorMsgState, setErrorMsgState) = useState<String?>(null)
    val (dataState, setDataState) = useState<OutboundOrderDetailed?>(null)

    // other hooks
    val params = useParams()

    // vars
    val recordId = params.get("recordId")?.toInt()
    val allowedFields: Array<String> = dataState?.parseOriginalICSFieldNames() ?: arrayOf()

    // requests
    val getDataRequest: () -> Unit = {
        launch {
            val response = ApiService.getOutboundOrderDetailed(props.session, props.sessionSetter, recordId!!)

            if (response.success) {
                setErrorMsgState(null)
                setDataState(response.data)

                response.refreshedSession?.let {
                    props.sessionSetter(it)
                }

            } else {
                setErrorMsgState(response.errorMsg ?: "An unexpected error occurred")
            }
        }
    }

    // functions to call after render
    useEffect {
        if (recordId != null && dataState == null && errorMsgState == null) getDataRequest()
    }

    // content container
    Grid {
        container = true
        spacing = responsive(2)

        // error alert
        if (!errorMsgState.isNullOrEmpty()) {
            Grid {
                item = true
                xs = 12

                Alert {
                    severity = AlertColor.error

                    +errorMsgState
                }
            }
        }

        // show data if available as information cards in grid
        if (dataState != null && errorMsgState == null) {
            // order information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Order information"
                    cardData = dataState
                    fieldsSequencedToShow = OutboundDetailPageFieldMappings.Order.orderInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = OutboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // customer information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Customer information"
                    cardData = dataState
                    fieldsSequencedToShow = OutboundDetailPageFieldMappings.Order.customerInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = OutboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // status information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Status information"
                    cardData = dataState
                    fieldsSequencedToShow = OutboundDetailPageFieldMappings.Order.statusInformationCard.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = OutboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // delivery information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Delivery information"
                    cardData = dataState
                    fieldsSequencedToShow = OutboundDetailPageFieldMappings.Order.deliverInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = OutboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // extra information card as Grid item
            Grid {
                item = true
                xs = 6

                VerticalDetailCard {
                    title = "Extra information"
                    cardData = dataState
                    fieldsSequencedToShow = OutboundDetailPageFieldMappings.Order.extraInformationCardHeaders.filter {
                        dataState.parseOriginalICSFieldNames().contains(it)
                    }.toTypedArray()
                    contentFieldMapping = OutboundDetailPageFieldMappings.Order.fieldMapping
                }
            }

            // Orderlines card
            Grid {
                item = true
                xs = 12

                TableCard {
                    title = "Orderlines"

                    AutoTable {
                        tableData = dataState.lines!!
                        fieldsSequencedToShow = OutboundDetailPageFieldMappings.OrderLines.headerSequence.filter {
                            dataState.parseOriginalICSFieldNames().contains(it)
                        }.toTypedArray()
                        headers = OutboundDetailPageFieldMappings.OrderLines.fieldMapping
                        limit = 25
                        limitOptions = arrayOf(25, 50, 100)
                        search = true
                        dense = true
                    }
                }
            }
        } else StyledBackDrop()
    }
}